/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 *
 * Misma cáscara que POS «Venta rápida» (Varios): ModalShell + formPanel + footer Cancelar/CTA.
 */
import React, { useRef } from 'react';
import { ActivityIndicator, Text, TextInput, View, type TextInput as TextInputType } from 'react-native';
import { ModalShell } from '../../../shared/modals/ModalShell'
import { ModalFormField } from '../../../shared/modals/ModalFormField'
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { ticketColors } from '../../../shared/theme/ticketColors'
import { TicketButton } from '../../../shared/ticket/TicketButton';

export type SettingsFormField = {
  key: string;
  label: string;
  value: string;
  placeholder?: string;
  multiline?: boolean;
  required?: boolean;
};

type Props = {
  visible: boolean;
  title: string;
  subtitle?: string;
  fields: SettingsFormField[];
  saving?: boolean;
  saveLabel?: string;
  onClose: () => void;
  onFieldChange: (key: string, value: string) => void;
  onSave: () => void;
};

export function SettingsFormEditModal({
  visible,
  title,
  subtitle,
  fields,
  saving = false,
  saveLabel = 'Guardar',
  onClose,
  onFieldChange,
  onSave,
}: Props) {
  const handleClose = () => {
    if (!saving) onClose();
  };

  const hasMultiline = fields.some((field) => field.multiline);
  const inputRefs = useRef<(TextInputType | null)[]>([]);

  return (
    <ModalShell
      visible={visible}
      onClose={handleClose}
      title={title}
      subtitle={subtitle}
      closeAccessibilityLabel={`Cerrar ${title}`}
      scrollable={hasMultiline}
      footer={
        <View style={modalExperience.footerActions}>
          <View style={{ flex: 1 }}>
            <TicketButton
              variant="ghost"
              label="Cancelar"
              onPress={handleClose}
              disabled={saving}
              accessibilityLabel="Cancelar"
            />
          </View>
          <View style={{ flex: 1 }}>
            <TicketButton
              variant="gold"
              label={saving ? 'Guardando…' : saveLabel}
              onPress={onSave}
              disabled={saving}
              accessibilityLabel={saveLabel}
            />
          </View>
        </View>
      }
    >
      <View style={modalExperience.formPanel}>
        {fields.map((field, index) => (
          <ModalFormField key={field.key} label={field.label} required={field.required}>
            <TextInput
              ref={(ref) => { inputRefs.current[index] = ref; }}
              style={[
                modalExperience.formInput,
                field.multiline ? modalExperience.formInputMultiline : null,
              ]}
              value={field.value}
              onChangeText={(text) => onFieldChange(field.key, text)}
              placeholder={field.placeholder}
              placeholderTextColor={ticketColors.ink3}
              editable={!saving}
              autoFocus={index === 0}
              multiline={field.multiline}
              textAlignVertical={field.multiline ? 'top' : 'center'}
              returnKeyType={field.multiline ? 'default' : index < fields.length - 1 ? 'next' : 'done'}
              onSubmitEditing={() => {
                if (!field.multiline && index < fields.length - 1) {
                  inputRefs.current[index + 1]?.focus();
                }
              }}
            />
          </ModalFormField>
        ))}
      </View>
    </ModalShell>
  );
}
